import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import api from '../lib/api';
import { ExchangeRateCard } from './PlatformPlans';

/** Every price DutiLyne charges, editable by platform staff. */
export const PlatformPricing = () => {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get('/platform/pricing')
      .then(r => { setData(r.data); setDraft(JSON.parse(JSON.stringify(r.data))); })
      .catch(() => toast.error('Could not load pricing'));
  };
  useEffect(load, []);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        tiers: Object.fromEntries(Object.entries(draft.tiers).map(([k, t]) => [k,
          { monthly_ghs: Number(t.monthly_ghs), annual_ghs: Number(t.annual_ghs) }])),
        bundles: Object.fromEntries(Object.entries(draft.bundles).map(([k, b]) => [k,
          { units: Number(b.units), price_ghs: Number(b.price_ghs) }])),
        branch_slot: { price_ghs: Number(draft.branch_slot.price_ghs) },
      };
      const { data: saved } = await api.put('/platform/pricing', payload);
      setData(saved); setDraft(JSON.parse(JSON.stringify(saved)));
      toast.success('Pricing updated');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save pricing'); }
    finally { setBusy(false); }
  };

  if (!draft) return <div className="pc-muted" data-testid="pc-pricing-loading"><Loader2 className="h-4 w-4 animate-spin" /></div>;

  const field = (value, onChange, testId) => (
    <input className="pc-input" type="number" min="1" value={value}
      onChange={(e) => onChange(e.target.value)} data-testid={testId} />
  );

  return (
    <div data-testid="pc-pricing">
      <ExchangeRateCard />
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
        <div>
          <h1 className="pc-h1">Pricing</h1>
          <p className="pc-sub">
            Plan tiers, add-on bundles and extra locations. Changes apply to the next checkout or renewal.
            {data?.updated_at && ` Last changed by ${data.updated_by_name || 'DutiLyne'}.`}
          </p>
        </div>
        <button className="pc-btn pc-btn-primary" onClick={save} disabled={busy} data-testid="pc-pricing-save">
          {busy ? 'Saving…' : 'Save pricing'}
        </button>
      </div>

      <div className="pc-card" style={{ marginTop: '1.1rem' }}>
        <div className="pc-card-h"><h2>Plan tiers (GHS)</h2></div>
        <table className="pc-table">
          <thead><tr><th>Tier</th><th>Monthly</th><th>Annual</th><th>Locations included</th></tr></thead>
          <tbody>
            {Object.entries(draft.tiers).map(([key, tier]) => (
              <tr key={key} data-testid={`pc-price-tier-${key}`}>
                <td><b style={{ color: '#fff' }}>{tier.name}</b></td>
                <td>{field(tier.monthly_ghs, v => setDraft(d => ({ ...d, tiers: { ...d.tiers, [key]: { ...tier, monthly_ghs: v } } })), `pc-price-${key}-monthly`)}</td>
                <td>{field(tier.annual_ghs, v => setDraft(d => ({ ...d, tiers: { ...d.tiers, [key]: { ...tier, annual_ghs: v } } })), `pc-price-${key}-annual`)}</td>
                <td className="pc-mono">{tier.branches}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="pc-card" style={{ marginTop: '1.1rem' }}>
        <div className="pc-card-h"><h2>Add-on bundles</h2></div>
        <table className="pc-table">
          <thead><tr><th>Add-on</th><th>Units per bundle</th><th>Price (GHS)</th></tr></thead>
          <tbody>
            {Object.entries(draft.bundles).map(([key, bundle]) => (
              <tr key={key} data-testid={`pc-price-bundle-${key}`}>
                <td><b style={{ color: '#fff' }}>{bundle.label}</b></td>
                <td>{field(bundle.units, v => setDraft(d => ({ ...d, bundles: { ...d.bundles, [key]: { ...bundle, units: v } } })), `pc-price-${key}-units`)}</td>
                <td>{field(bundle.price_ghs, v => setDraft(d => ({ ...d, bundles: { ...d.bundles, [key]: { ...bundle, price_ghs: v } } })), `pc-price-${key}-price`)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="pc-card" style={{ marginTop: '1.1rem' }}>
        <div className="pc-card-h"><h2>Extra location</h2></div>
        <div style={{ padding: '.9rem', maxWidth: 320 }}>
          <label className="pc-field">Price per extra location, per month (GHS)</label>
          {field(draft.branch_slot.price_ghs,
            v => setDraft(d => ({ ...d, branch_slot: { ...d.branch_slot, price_ghs: v } })),
            'pc-price-branch-slot')}
          <p className="pc-sub" style={{ marginTop: '.6rem' }}>
            Renews with the pharmacy's subscription. A paid location still needs branch approval.
          </p>
        </div>
      </div>
    </div>
  );
};
